/**
 * useElementWidth Hook
 * Measures the width of an element using ResizeObserver
 * Similar to online-community's useElementBox but simplified for width only
 */

import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Hook that tracks the width of a DOM element
 * @returns {[RefObject<HTMLDivElement>, number]} A tuple where the first item is a ref to the target
 * element and the second item is the current width of that element in pixels
 */
export const useElementWidth = (): [RefObject<HTMLDivElement>, number] => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    // Set initial width
    setWidth(element.getBoundingClientRect().width);

    // Create ResizeObserver to track width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === element) {
          setWidth(entry.contentRect.width);
        }
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return [ref, width];
};

